import './scss/style.scss';
import React, { Suspense, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Provider as StoreProvider } from 'react-redux';
// Dayjs for Handling DateTime
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import relativeTime from 'dayjs/plugin/relativeTime';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { QueryClient, QueryClientProvider } from 'react-query';
import { persistQueryClient } from 'react-query/persistQueryClient-experimental';
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental';

import ProtectedRoute from './utils/ProtectedRoute';
import Unauthorized from './components/UnauthorizedComps/Unauthorized';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { ToastContainer } from 'react-toastify';
import { store } from './store';
import { AppProviders } from './app/index';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

/**
 * Persists only the Report & Analytics queries (keyed `report-analytics-*`) to
 * localStorage, so navigating back to that page after a full page reload can render
 * from cache instantly instead of waiting on the network again. Every other page's
 * query cache is untouched — it still lives in memory only, same as before.
 *
 * `buster` is a manual cache-version marker: bump it if the API response shape for any
 * report-analytics module ever changes, to discard old cached shapes instead of trying
 * to render them.
 */
const REPORT_ANALYTICS_CACHE_KEY = 'amanahfy-report-analytics-cache';
const hasSession = () => Boolean(window.localStorage.getItem('authToken'));
const reportAnalyticsPersistor = createWebStoragePersistor({
  storage: {
    getItem: (key) => window.localStorage.getItem(key),
    // Recheck when the throttled write runs: logout may happen after it was queued.
    setItem: (key, value) => (hasSession() ? window.localStorage.setItem(key, value) : window.localStorage.removeItem(key)),
    removeItem: (key) => window.localStorage.removeItem(key),
  },
  key: REPORT_ANALYTICS_CACHE_KEY,
});

// Query keys contain the auth token and report data contains donor PII.
// Never persist or restore this cache without a session.
persistQueryClient({
  queryClient,
  persistor: {
    persistClient: (client) => (hasSession() ? reportAnalyticsPersistor.persistClient(client) : reportAnalyticsPersistor.removeClient()),
    restoreClient: () => (hasSession() ? reportAnalyticsPersistor.restoreClient() : reportAnalyticsPersistor.removeClient()),
    removeClient: () => reportAnalyticsPersistor.removeClient(),
  },
  maxAge: 24 * 60 * 60 * 1000,
  buster: '2', // Discard report responses with the old trailing-30-day revenue footer.
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('report-analytics-'),
  },
});

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

// Pages
const Login = React.lazy(() => import('./pages/login/Login'));
const Logout = React.lazy(() => import('./pages/logout/Logout'));
const Register = React.lazy(() => import('./pages/register/Register'));
const ResetPassword = React.lazy(() => import('./pages/reset-password/ResetPassword'));
const CreatePassword = React.lazy(() => import('./pages/create-password/CreatePassword'));
const VerifyEmail = React.lazy(() => import('./pages/verify-email/index'));
const Page404 = React.lazy(() => import('./pages/page404/Page404'));
const Terms = React.lazy(() => import('./pages/terms/terms'));
// const Onboarding = React.lazy(() => import('./pages/onboarding'));
const Page500 = React.lazy(() => import('./pages/page500/Page500'));

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(relativeTime);

dayjs.updateLocale('en', {
  weekStart: 1,
});

const App = () => (
  <Router>
    <Suspense fallback={<LoadingIndicator />}>
      <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
        <StoreProvider store={store}>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <AppProviders>
                <AppRoutes />
              </AppProviders>
            </QueryClientProvider>
          </AuthProvider>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss={true}
            draggable={true}
            pauseOnHover={true}
            theme="colored"
            toastClassName="font-fam-sfprodisplay text-xsm-4"
          />
        </StoreProvider>
      </GoogleReCaptchaProvider>
    </Suspense>
  </Router>
);

const LoadingIndicator = () => (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

const AppRoutes = () => {
  const { authToken, userData } = useContext(AuthContext);

  const isLoggedIn = authToken;

  return (
    <Routes>
      <Route path="/login" element={isLoggedIn ? <Navigate to="/" /> : <Login />} />
      <Route path="/register" element={isLoggedIn ? <Navigate to="/" /> : <Register />} />
      <Route path="/reset-password" element={isLoggedIn ? <Navigate to="/" /> : <ResetPassword />} />
      <Route path="/create-password" element={<CreatePassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/onboarding" element={<Navigate to="/" />} />
      <Route path="/404" element={<Page404 />} />
      <Route path="/500" element={<Page500 />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/terms" element={<ProtectedRoute isLoggedIn={isLoggedIn} component={Terms} />} />
      <Route path="*" element={<ProtectedRoute isLoggedIn={isLoggedIn} component={DefaultLayout} />} />
    </Routes>
  );
};

export default App;
